/**
 * JSON model ↔ CBOR form (F6). Lexicon validators consume {$bytes: stdBase64};
 * signing/wire consume Uint8Array. This file is the only place that conversion lives.
 */
import { CborFloat, type CborValue } from "@beacon/crypto";

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const B64_RE = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

export function stdB64Encode(b: Uint8Array): string {
  let out = "";
  for (let i = 0; i < b.length; i += 3) {
    const n = (b[i] << 16) | ((b[i + 1] ?? 0) << 8) | (b[i + 2] ?? 0);
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
    out += i + 1 < b.length ? B64[(n >> 6) & 63] : "=";
    out += i + 2 < b.length ? B64[n & 63] : "=";
  }
  return out;
}

export function stdB64Decode(s: string): Uint8Array {
  if (!B64_RE.test(s)) throw new Error("model: invalid standard base64");
  const pad = s.endsWith("==") ? 2 : s.endsWith("=") ? 1 : 0;
  const out = new Uint8Array((s.length / 4) * 3 - pad);
  let o = 0;
  for (let i = 0; i < s.length; i += 4) {
    const n =
      (B64.indexOf(s[i]) << 18) | (B64.indexOf(s[i + 1]) << 12) |
      ((s[i + 2] === "=" ? 0 : B64.indexOf(s[i + 2])) << 6) |
      (s[i + 3] === "=" ? 0 : B64.indexOf(s[i + 3]));
    out[o++] = (n >> 16) & 255;
    if (o < out.length) out[o++] = (n >> 8) & 255;
    if (o < out.length) out[o++] = n & 255;
  }
  return out;
}

function isBytesJson(v: unknown): v is { $bytes: string } {
  return (
    typeof v === "object" && v !== null && !Array.isArray(v) &&
    Object.keys(v).length === 1 && typeof (v as { $bytes?: unknown }).$bytes === "string"
  );
}

/** JSON model → CBOR form: {$bytes: b64} → Uint8Array, recursively. */
export function toCborModel(v: unknown): CborValue {
  if (isBytesJson(v)) return stdB64Decode(v.$bytes);
  if (Array.isArray(v)) return v.map(toCborModel);
  if (typeof v === "object" && v !== null) {
    const out: Record<string, CborValue> = {};
    for (const [k, val] of Object.entries(v)) out[k] = toCborModel(val);
    return out;
  }
  return v as CborValue;
}

/** CBOR form → JSON model: Uint8Array → {$bytes: b64}, recursively. */
export function toJsonModel(v: CborValue): unknown {
  if (v instanceof CborFloat) return v.value;
  if (v instanceof Uint8Array) return { $bytes: stdB64Encode(v) };
  if (Array.isArray(v)) return v.map(toJsonModel);
  if (typeof v === "object" && v !== null) {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) out[k] = toJsonModel(val);
    return out;
  }
  return v;
}

/** Clock instant (µs) → RFC 3339 UTC. new Date(value) is lint-allowed: formatting, not reading time. */
export function rfc3339FromMicros(us: number): string {
  if (!Number.isSafeInteger(us) || us < 0) throw new Error("model: bad micros");
  const iso = new Date(Math.floor(us / 1000)).toISOString(); // "….sssZ"
  const frac = us % 1_000_000;
  const head = iso.slice(0, 19); // "YYYY-MM-DDTHH:MM:SS"
  return frac === 0 ? `${head}Z` : `${head}.${frac.toString().padStart(6, "0")}Z`;
}

// Lexicon `datetime` format: RFC 3339, UTC-`Z`-only (mirrors packages/lexicon runtime).
const RFC3339_UTC_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?Z$/;

/** Coerce an upstream RFC3339-ish observation time into the lexicon's canonical UTC-`Z` datetime,
 *  or undefined when it's absent/empty/unparseable. Inputs already in UTC-`Z` form pass through
 *  byte-identically (golden-stable for fogos/FIRMS); a local offset such as a warnung.bund.de CAP
 *  `sent` ("…+02:00") is converted to UTC. Pure: new Date(value) formats a given instant — it reads
 *  no clock (ADR-13 gate-30 allows the arg form). */
export function rfc3339ToUtc(s: string | undefined): string | undefined {
  if (!s) return undefined;
  if (RFC3339_UTC_RE.test(s)) return s;
  const ms = new Date(s).getTime();
  if (!Number.isSafeInteger(ms) || ms < 0) return undefined;
  return rfc3339FromMicros(ms * 1000);
}
